import axios from 'axios';

import { config } from '@/config';
import { trackEvent } from '@/services/monitoring/analytics';

/** The slice of the AWS WAF intelligent threat SDK (`challenge.js`) this app uses. */
interface AwsWafIntegration {
  getToken(): Promise<string>;
}

export interface RenderCaptchaOptions {
  apiKey: string;
  onSuccess: (wafToken: string) => void;
  onError: (error: unknown) => void;
  dynamicWidth?: boolean;
  skipTitle?: boolean;
  defaultLocale?: string;
}

/** The slice of the AWS WAF CAPTCHA SDK (`jsapi.js`) this app uses. */
export interface AwsWafCaptcha {
  renderCaptcha(container: HTMLElement, options: RenderCaptchaOptions): void;
}

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegration;
    AwsWafCaptcha?: AwsWafCaptcha;
  }
}

const TIMEOUT_MS = 5000;
const TOKEN_HEADER = 'x-aws-waf-token';
// Set by the WAF on its CAPTCHA (405) and challenge (202) responses.
const ACTION_HEADER = 'x-amzn-waf-action';

function withTimeout<T>(promise: Promise<T>): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), TIMEOUT_MS)),
  ]);
}

/** Inject a script once and resolve to the global it defines, or null if it never arrives. */
function scriptLoader<T>(src: () => string, global: () => T | undefined) {
  let loading: Promise<T | null> | null = null;
  return (): Promise<T | null> => {
    // A script that outlived an earlier timeout may have arrived since; don't load it twice.
    const existing = global();
    if (existing) return Promise.resolve(existing);
    loading ??= withTimeout(
      new Promise<T | null>((resolve) => {
        const script = document.createElement('script');
        script.src = src();
        script.async = true;
        script.onload = () => resolve(global() ?? null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      })
    ).then((loaded) => {
      // Let a later call retry, e.g. after a flaky network or a blocker being switched off.
      if (!loaded) loading = null;
      return loaded;
    });
    return loading;
  };
}

const loadIntegration = scriptLoader(
  () => `${config.waf.integrationUrl}challenge.js`,
  () => window.AwsWafIntegration
);

const loadCaptcha = scriptLoader(
  () => `${config.waf.captchaUrl}jsapi.js`,
  () => window.AwsWafCaptcha
);

/**
 * Load the AWS WAF SDK once. It runs the silent browser challenge and starts collecting the
 * interaction signals the WAF's bot rules score, so call it as soon as a protected form mounts.
 * Resolves to null when the SDK isn't configured (outside production) or can't load.
 */
export function loadWafSdk(): Promise<AwsWafIntegration | null> {
  return config.waf.integrationUrl ? loadIntegration() : Promise.resolve(null);
}

/** Load the CAPTCHA SDK; null when it isn't configured or can't load. */
export function loadCaptchaSdk(): Promise<AwsWafCaptcha | null> {
  return config.waf.captchaUrl && config.waf.captchaApiKey ? loadCaptcha() : Promise.resolve(null);
}

/**
 * Headers carrying the WAF token for a request the WAF's bot rules inspect. The SDK also sets the
 * `aws-waf-token` cookie; the header is sent too so the token goes even if the cookie doesn't.
 * Empty when there is no token: the request still goes, and the WAF decides what to do with it.
 */
export async function wafTokenHeaders(): Promise<Record<string, string>> {
  const integration = await loadWafSdk();
  if (!integration) return {};
  try {
    const token = await withTimeout(integration.getToken());
    return token ? { [TOKEN_HEADER]: token } : {};
  } catch {
    return {};
  }
}

/** What the WAF asked of a request it stopped: a CAPTCHA, a silent challenge, or a flat block. */
export function wafResponse(error: unknown): 'captcha' | 'challenge' | 'block' | null {
  if (!axios.isAxiosError(error) || !error.response) return null;
  const action = error.response.headers?.[ACTION_HEADER];
  if (action === 'captcha' || action === 'challenge') return action;
  // The API's own 403s are JSON (DRF), so an HTML 403 can only be the WAF's block page.
  return error.response.status === 403 && typeof error.response.data === 'string' ? 'block' : null;
}

/** The person closed the CAPTCHA instead of solving it. */
export class WafCaptchaCancelled extends Error {
  constructor() {
    super('The security check was closed before it was solved.');
    this.name = 'WafCaptchaCancelled';
  }
}

export interface CaptchaRequest {
  solve(token: string): void;
  cancel(): void;
}

let captchaRequest: CaptchaRequest | null = null;
const captchaListeners = new Set<() => void>();

function setCaptchaRequest(request: CaptchaRequest | null) {
  captchaRequest = request;
  captchaListeners.forEach((listener) => listener());
}

/** For `WafCaptchaDialog`: the CAPTCHA waiting to be shown, if any. */
export const captchaRequests = {
  subscribe(listener: () => void) {
    captchaListeners.add(listener);
    return () => captchaListeners.delete(listener);
  },
  current: () => captchaRequest,
};

/**
 * Ask the person to solve a WAF CAPTCHA and resolve to the token it yields. Needs a mounted
 * `WafCaptchaDialog`; rejects with `WafCaptchaCancelled` if they close it.
 */
export function solveCaptcha(): Promise<string> {
  captchaRequest?.cancel();
  return new Promise((resolve, reject) => {
    const request: CaptchaRequest = {
      solve: (token) => {
        if (captchaRequest !== request) return;
        setCaptchaRequest(null);
        resolve(token);
      },
      cancel: () => {
        if (captchaRequest !== request) return;
        setCaptchaRequest(null);
        reject(new WafCaptchaCancelled());
      },
    };
    setCaptchaRequest(request);
  });
}

/** Which bot-checked request went through `wafProtected`, for analytics. */
export type WafProtectedRequest = 'signup' | 'signup_resend';

/**
 * Send a request the WAF bot-checks, with the WAF token. If the WAF answers with a CAPTCHA, show
 * it and send once more with the token it yields; if with a challenge (no token reached it), mint
 * one and send once more. Anything else, including a second refusal, is thrown to the caller.
 */
export async function wafProtected<T>(
  request: WafProtectedRequest,
  send: (headers: Record<string, string>) => Promise<T>
): Promise<T> {
  const refused = (error: unknown) => {
    const action = wafResponse(error);
    if (action) trackEvent('waf_request_refused', { request, action });
    return error;
  };
  // The one retry: whatever the WAF says to it goes back to the caller.
  const retry = (headers: Record<string, string>) =>
    send(headers).catch((error: unknown) => Promise.reject(refused(error)));

  try {
    return await send(await wafTokenHeaders());
  } catch (error) {
    const action = wafResponse(error);
    if (action === 'captcha' && captchaListeners.size > 0 && (await loadCaptchaSdk())) {
      trackEvent('waf_captcha_shown', { request });
      let token: string;
      try {
        token = await solveCaptcha();
      } catch (cancelled) {
        trackEvent('waf_captcha_cancelled', { request });
        throw cancelled;
      }
      trackEvent('waf_captcha_solved', { request });
      return retry({ [TOKEN_HEADER]: token });
    }
    if (action === 'challenge') {
      const headers = await wafTokenHeaders();
      if (headers[TOKEN_HEADER]) return retry(headers);
    }
    throw refused(error);
  }
}
